import type { Analysis } from "./analysis";
import { type ChordView, display } from "./chords";
import { linesFromText } from "./pairededit";

/**
 * Render an authored chart, rather than re-deriving one from the analysis.
 *
 * Once someone has edited a song, what they typed IS the chart. Every word and
 * every chord position comes from the author.
 *
 * The clock is a separate question. Two answers, best first:
 *
 * 1. **Measured timings** (`ChartTimings`), written by the worker after a save.
 *    It re-runs forced alignment over the audio using the words the person
 *    actually typed, so the highlight follows the singing of *their* text.
 * 2. **The analysis, by position** — the fallback while a re-measurement is
 *    still queued, and after one that could not be made. Line times are right,
 *    word times inside a line are interpolated, so a rewritten line drifts.
 *
 * Deriving the display from the analysis instead meant a corrected song kept
 * showing the machine's own transcription, so fixing the words changed nothing
 * anyone could see.
 */

/** Word timings measured against the recording for this exact chart. */
export interface ChartTimings {
  clock: string;
  lines: { t: number; end: number | null; words: { t: number; w: string }[] }[];
}

export interface ChartRow {
  kind: "lyric" | "instrumental";
  from: number;
  to: number;
  label?: string;
  chordRow: string;
  marks: { column: number; label: string; beat: number; endBeat: number }[];
  words: { text: string; t: number }[];
}

/** The beat nearest a moment, and the moment a beat falls on. */
function beatAt(analysis: Analysis, seconds: number): number {
  let best = 0;
  let closest = Infinity;
  for (const beat of analysis.beats) {
    const away = Math.abs(beat.t - seconds);
    if (away < closest) { closest = away; best = beat.i; }
  }
  return best;
}

export function rowsFromChart(
  text: string, analysis: Analysis, view: ChordView,
  timings?: ChartTimings | null,
): ChartRow[] {
  const authored = linesFromText(text);
  // Measured timings are per authored lyric line, in order, so they line up
  // with this chart and not with the machine's transcription of it.
  const measured = timings?.lines?.length ? timings.lines : null;
  const sung = analysis.lyrics.lines;
  const rows: ChartRow[] = [];

  // Authored lyric lines take their timing from the analysis line in the same
  // position. Editing words never moves a line, so this holds; a line added
  // where the recording has none inherits the previous line's time, which puts
  // it roughly right rather than exactly.
  let lyricIndex = 0;
  let lastTime = 0;

  for (const line of authored) {
    if (line.instrumental) {
      const at = lastTime;
      rows.push({
        kind: "instrumental",
        from: at,
        to: at,
        label: "Instrumental",
        chordRow: line.chords.map((c) => display(c.name, view)).join("   "),
        marks: line.chords.map((chord, index) => ({
          column: index * 6,
          label: display(chord.name, view),
          beat: beatAt(analysis, at),
          endBeat: beatAt(analysis, at),
        })),
        words: [],
      });
      continue;
    }

    const timed = measured?.[lyricIndex] ?? null;
    const reference = measured ? null : sung[lyricIndex];
    const start = timed?.t ?? reference?.t ?? lastTime;
    const end = timed?.end ?? reference?.end ?? start;
    lyricIndex += 1;
    lastTime = end || start;

    const pieces = line.text.split(/(\s+)/);
    const words: { text: string; t: number }[] = [];
    let column = 0;
    let wordNumber = 0;
    const columnOf: number[] = [];
    for (const piece of pieces) {
      if (piece.trim()) {
        // A measured timing is for this word in this chart, so it is taken as
        // it stands. Without one, the position within the line is all there is
        // to go on — right for a line nobody touched, and visibly wrong for a
        // line that was rewritten, which is why the worker re-measures.
        const exact = timed?.words?.[wordNumber];
        const spread = reference && reference.words?.length
          ? start + ((end - start) * wordNumber) / reference.words.length
          : start + ((end - start) * wordNumber) / Math.max(pieces.length, 1);
        words.push({ text: piece, t: exact?.t ?? reference?.words?.[wordNumber]?.t ?? spread });
        columnOf.push(column);
        wordNumber += 1;
      }
      column += piece.length;
    }

    const marks = line.chords.map((chord) => {
      // Which word is this chord over? The nearest by column, which is how a
      // reader decides too.
      let nearest = 0;
      let distance = Infinity;
      columnOf.forEach((at, index) => {
        const away = Math.abs(at - chord.at);
        if (away < distance) { distance = away; nearest = index; }
      });
      const moment = words[nearest]?.t ?? start;
      const beat = beatAt(analysis, moment);
      return { column: chord.at, label: display(chord.name, view), beat, endBeat: beat };
    });

    let chordRow = "";
    for (const mark of [...marks].sort((a, b) => a.column - b.column)) {
      chordRow = chordRow.length > mark.column
        ? `${chordRow} ${mark.label}`
        : chordRow.padEnd(mark.column) + mark.label;
    }

    rows.push({ kind: "lyric", from: start, to: end || start, chordRow, marks, words });
  }

  return rows;
}
